// Libraries
import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
// Utils
import { CommonContext } from "config/commonProvider";
import { ANIMALS_TABLE_ACTION_TYPES } from "constants/Animals";
import { TABLE_IDS } from "constants/Interface";
import { getBUFromLocalStorage } from "helpers/storage";
import { useHasFeature } from "hooks";
// API
import { GetGroup_group } from "api/MyLivestock/Groups/types/GetGroup";
import { GET_GROUP_BY_ID } from "api/MyLivestock/Groups/queries";
import { ASSIGN_ANIMALS_TO_GROUP } from "api/MyLivestock/Groups/mutations";
import { AssignAnimalsToGroup, AssignAnimalsToGroupVariables } from "api/MyLivestock/Groups/types/AssignAnimalsToGroup";
import { GetAnimalsList_animalsExtended_animals } from "api/MyLivestock/Animal/types/GetAnimalsList";
// Components
import ConfirmMoveToFieldModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToFieldModal";
import ConfirmMoveToGroupModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToGroupModal";
import { ConfirmModalWithOutInput as RemoveAnimalConfirmModal, EllipsisMenu } from "components";
import { ConfirmMarkOffFarmModal } from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMarkOffFarmModal";
import { ConfirmAddPreviousKeeperModal } from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmAddPreviousKeeperModal";
import { LogActivityModal } from "components/MyLivestock/Animals/AnimalBulkActions/LogActivityModal";
import { MarkAsDeadModal } from "animals/MarkAsDeadModal";

interface Props {
  group?: GetGroup_group;
  animal: GetAnimalsList_animalsExtended_animals;
  error?: string;
  isFarmWithGroups: boolean;
}

const TableActionColumn: React.FC<Props> = ({ animal, group, isFarmWithGroups }) => {
  const { showNotification } = useContext(CommonContext);
  const { groupId } = useParams<{ groupId: string }>();
  const isShowLogActivity: boolean = useHasFeature("SHOW_LOG_ACTIVITY");

  const [error, setError] = useState<string | null>(null);
  const [showModal, setModalShow] = useState<string | undefined>();

  const [assignAnimals, { loading: requesting }] = useMutation<AssignAnimalsToGroup, AssignAnimalsToGroupVariables>(
    ASSIGN_ANIMALS_TO_GROUP,
  );

  const ANIMALS_TABLE_ACTIONS = [
    {
      key: "add_to_group",
      title: "Add to group",
    },
    {
      key: "move_to_field",
      title: "Move to location",
    },
    {
      key: "mark_as_dead",
      title: "Mark as dead",
    },

    {
      key: "mark_off_farm",
      title: "Mark off-farm",
    },
    {
      key: "add_previous_keeper",
      title: "Add previous keeper",
    },
    {
      key: "log_activity",
      title: "Log activity",
    },
  ];

  const selectedAnimal = group
    ? {
        ...animal,
        group,
      }
    : animal;

  const removeAnimalFromGroup = async (): Promise<void> => {
    setError(null);

    try {
      const { data } = await assignAnimals({
        variables: {
          input: {
            animals: [animal.id],
            date: new Date(),
          },
        },
        refetchQueries: [
          {
            query: GET_GROUP_BY_ID,
            variables: {
              group: groupId,
              businessUnit: getBUFromLocalStorage(),
            },
          },
        ],
      });

      if (data?.moveAnimalsToGroup?.errors) {
        setError(data?.moveAnimalsToGroup?.errors[0]?.message || "Error");
        showNotification({
          variant: "error",
          message: "Error removing animal",
        });
      } else {
        showNotification({
          message: "Animal removed successfully",
        });
        setModalShow(undefined);
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Error removing animal",
      });
    }
  };

  const handleRemoveAnimalFromGroup = (): void => {
    removeAnimalFromGroup();
  };

  const handleHideModal = (): void => {
    setModalShow(undefined);
  };

  const handleCloseModal = (): void => {
    handleHideModal();
    setError(null);
  };

  const handleTooltipPress = (e: React.MouseEvent, key: string): void => {
    e.stopPropagation();
    setModalShow(key);
  };

  const isAnimalInGroup = group || animal.group;

  const actions = ANIMALS_TABLE_ACTIONS.map((action) => ({
    ...action,
    caption: isAnimalInGroup && action.key === ANIMALS_TABLE_ACTION_TYPES.ADD_TO_GROUP ? "Change group" : action.title,
    disabled: !isFarmWithGroups && action.key === ANIMALS_TABLE_ACTION_TYPES.ADD_TO_GROUP,
    onClick: (event): void => handleTooltipPress(event, action.key),
  }));
  const filteredActions = actions.filter(
    (item) =>
      !(
        (item.key === ANIMALS_TABLE_ACTION_TYPES.MARK_OFF_FARM && animal.rflId) ||
        (item.key === ANIMALS_TABLE_ACTION_TYPES.ADD_PREVIOUS_KEEPER && animal.hasPreviousKeeperInfo) ||
        (item.key === ANIMALS_TABLE_ACTION_TYPES.LOG_ACTIVITY && !isShowLogActivity)
      ),
  );

  return (
    <div id={animal.id}>
      <EllipsisMenu actions={filteredActions} />
      {showModal === ANIMALS_TABLE_ACTION_TYPES.MARK_OFF_FARM && (
        <ConfirmMarkOffFarmModal
          animals={[animal]}
          groupId={group?.id}
          isShowModal={showModal === ANIMALS_TABLE_ACTION_TYPES.MARK_OFF_FARM}
          isSingleAnimal
          onClose={handleHideModal}
          tableId={TABLE_IDS.ANIMALS}
        />
      )}
      {showModal === ANIMALS_TABLE_ACTION_TYPES.MARK_AS_DEAD && (
        <MarkAsDeadModal
          animalIds={[animal.id]}
          showModal={showModal === ANIMALS_TABLE_ACTION_TYPES.MARK_AS_DEAD}
          onClose={handleHideModal}
        />
      )}
      {showModal === ANIMALS_TABLE_ACTION_TYPES.ADD_PREVIOUS_KEEPER && (
        <ConfirmAddPreviousKeeperModal
          animals={[animal]}
          isShowModal={showModal === ANIMALS_TABLE_ACTION_TYPES.ADD_PREVIOUS_KEEPER}
          isSingleAnimal
          onClose={handleHideModal}
        />
      )}
      <RemoveAnimalConfirmModal
        error={error}
        requesting={requesting}
        title={`Are you sure you want to remove ${animal.passportNumber} from the group? This will not remove the animal from your inventory`}
        showModal={showModal === ANIMALS_TABLE_ACTION_TYPES.REMOVE_FROM_GROUP}
        onClose={handleCloseModal}
        onConfirm={handleRemoveAnimalFromGroup}
      />
      {showModal === ANIMALS_TABLE_ACTION_TYPES.ADD_TO_GROUP && (
        <ConfirmMoveToGroupModal
          animals={[selectedAnimal]}
          showModal={showModal === ANIMALS_TABLE_ACTION_TYPES.ADD_TO_GROUP}
          onClose={handleHideModal}
        />
      )}
      {showModal === ANIMALS_TABLE_ACTION_TYPES.MOVE_TO_FIELD && (
        <ConfirmMoveToFieldModal
          animals={[selectedAnimal.id]}
          showModal={showModal === ANIMALS_TABLE_ACTION_TYPES.MOVE_TO_FIELD}
          onClose={handleHideModal}
        />
      )}
      {showModal === ANIMALS_TABLE_ACTION_TYPES.LOG_ACTIVITY && (
        <LogActivityModal
          animals={[animal]}
          isShowModal={showModal === ANIMALS_TABLE_ACTION_TYPES.LOG_ACTIVITY}
          isSingleAnimal
          onClose={handleHideModal}
        />
      )}
    </div>
  );
};

export default TableActionColumn;
